import { useEffect, useRef, useState } from 'react';
import { useInView } from '@/hooks/useScrollReveal.ts';

/**
 * Inline number that counts up when scrolled into view.
 *
 * Renders the FINAL value in markup so prerendered HTML and no-JS visitors see
 * the real figure (the original shipped "0%+" and relied on JS to correct it,
 * which also caused a width change mid-animation).
 */
export function CountUp({
  value,
  suffix = '',
  duration = 2000,
}: {
  value: number;
  suffix?: string;
  duration?: number;
}) {
  const [display, setDisplay] = useState(value);
  const frame = useRef<number | null>(null);

  const ref = useInView<HTMLSpanElement>(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      // Cubic ease-out, matching the original solutions.html counter.
      setDisplay(Math.floor(value * (1 - (1 - p) ** 3)));
      if (p < 1) frame.current = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    frame.current = requestAnimationFrame(tick);
  });

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  return (
    <span ref={ref}>
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}
